import { useState } from 'react'

import { Loader2, Pencil, Save, X } from 'lucide-react'

import { ClassificationBadge } from '@/entities/client/ui/classification-badge'
import { Button } from '@/shared/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Client, ClientClassification } from '@monorepo/functions/src/types/client'

type ClientClassificationSelectorProps = {
    client: Client
    onClassificationChange: (classification: Client['classification']) => Promise<void>
}

export const ClientClassificationSelector = ({ client, onClassificationChange }: ClientClassificationSelectorProps) => {
    const [isSaving, setIsSaving] = useState(false)
    const [isEditing, setIsEditing] = useState(false)
    const [localClassification, setLocalClassification] = useState<ClientClassification>(client.classification)

    const handleClassificationChange = (value: Client['classification']) => setLocalClassification(value)

    const handleSave = async () => {
        try {
            setIsEditing(false)
            setIsSaving(true)
            await onClassificationChange(localClassification)
        } finally {
            setIsSaving(false)
        }
    }

    return (
        <div className='flex items-center gap-2'>
            {!isEditing && (
                <>
                    <ClassificationBadge classification={localClassification} />
                    {isSaving ? (
                        <Loader2 className='h-4 w-4 animate-spin text-primary' />
                    ) : (
                        <Button variant='ghost' size='icon' onClick={() => setIsEditing(true)} className='h-6 w-6 p-0'>
                            <Pencil className='h-4 w-4' />
                        </Button>
                    )}
                </>
            )}
            {isEditing && (
                <>
                    <Select value={localClassification} onValueChange={handleClassificationChange}>
                        <SelectTrigger className='w-[120px]'>
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value='normal'>Normal</SelectItem>
                            <SelectItem value='staff'>Staff</SelectItem>
                            <SelectItem value='VIP'>VIP</SelectItem>
                        </SelectContent>
                    </Select>

                    <div className='flex gap-1'>
                        <Button variant='ghost' size='icon' onClick={handleSave} className='h-6 w-6 p-0'>
                            <Save className='h-4 w-4' />
                        </Button>
                        <Button variant='ghost' size='icon' onClick={() => setIsEditing(false)} className='h-6 w-6 p-0'>
                            <X className='h-4 w-4' />
                        </Button>
                    </div>
                </>
            )}
        </div>
    )
}
